import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatDriverNotesAnswer {
  notes: string;
}

const DEFAULT_MAX_LENGTH = 300;

/**
 * Renders the driver-notes free-text widget inside the chat widget — used by
 * Groundspan/CMAC taxi bookings to optionally pass instructions to the driver
 * (e.g. late check-in, luggage, accessibility needs). Mirrors the manual
 * LightUK SI form's "Driver Notes" field (per-item supplementary schema's
 * `driverNotes`, format "textarea") — free text, optional, character-capped.
 */
@Component({
  selector: 'app-chat-driver-notes-input',
  standalone: false,
  templateUrl: './chat-driver-notes-input.component.html',
  styleUrls: ['./chat-driver-notes-input.component.scss']
})
export class ChatDriverNotesInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() savedValue: string | null = null;
  @Input() required = false;
  @Input() maxLength = DEFAULT_MAX_LENGTH;
  @Output() answer = new EventEmitter<ChatDriverNotesAnswer>();
  @Output() skip = new EventEmitter<void>();

  notes = '';
  /** Shown alongside the (disabled) field once this widget instance is locked. */
  chosenSummary: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the widget whenever the underlying field changes (new question)
    // — but NOT merely because `locked` toggled, which would wipe out the
    // entered text just as we want to display it as read-only.
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    this.notes = this.savedValue ?? '';
    this.chosenSummary = null;
  }

  get isMandatory(): boolean {
    return this.required === true;
  }

  get isValid(): boolean {
    return this.isMandatory ? this.notes.trim().length > 0 : true;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const trimmed = this.notes.trim();
    if (!trimmed) {
      // Only reachable for an optional field with nothing entered.
      this.chosenSummary = 'Skipped';
      this.skip.emit();
      return;
    }
    this.chosenSummary = trimmed;
    this.answer.emit({ notes: trimmed });
  }
}
